import { Search } from 'lucide-react'
import CommonDialog from 'components/modals/CommonDialog'
import { FC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Input, cn, Tabs, TabsContent, TabsControls } from '@wellesley/ui'
import ModalLoader from 'components/modals/ModalLoader'
import SkeletonList from 'components/Skeletons/UsersListSkeleton'
import {
  ConnectionModalProps,
  ConnectionsTabs,
  ConnectionTab,
  TabItem,
} from './types'
export * from './types'
import { getDisplayUsername } from '@wellesley/user'
import { ScrolledList, VirtualizedScrolledList } from '../ScrolledList'

const getTabDefaults = (tabItem: TabItem) => {
  return {
    label: tabItem === 'first' ? 'Followers' : 'Following',
    counter: 0,
  }
}

const getTabLabel = (tab: TabItem, tabs?: Record<TabItem, ConnectionTab>) => {
  const tabDefaults = getTabDefaults(tab)
  const tabName = tabs?.[tab]?.label || tabDefaults.label
  const tabCounter = tabs?.[tab]?.counter || tabDefaults.counter
  return `${tabName} (${tabCounter})`
}

const ConnectionsModal: FC<ConnectionModalProps> = ({
  activeTab,
  onTabChange,
  showLists = true,
  firstList,
  secondList,
  user,
  isLoading,
  isOpen,
  onClose,
  tabs,
  searchQuery,
  onSearchQueryChange,
  listClassName,
  dialogClassName,
  hiddenListMessage,
  renderFooter,
  title = user ? `Connections ${getDisplayUsername(user.username)}` : undefined,
  firstListInfiniteScroll,
  secondListInfiniteScroll,
  ...listProps
}) => {
  const methods = useForm()

  return (
    <CommonDialog
      open={isOpen}
      onCloseModal={onClose}
      title={title}
      className={cn('content-baseline', dialogClassName)}
      childrenClassName="p-none min-h-[500px] overflow-hidden max-h-[calc(90vh-65px)]"
      titleClassName="!border-none"
      renderFooter={renderFooter}
    >
      {isLoading ? (
        <ModalLoader />
      ) : (
        <Tabs
          className="h-full"
          defaultValue={activeTab}
          onValueChange={onTabChange}
        >
          <TabsControls
            className="!mt-0"
            items={[
              {
                value: ConnectionsTabs.first,
                label: getTabLabel('first', tabs),
              },
              {
                value: ConnectionsTabs.second,
                label: getTabLabel('second', tabs),
              },
            ]}
          />
          {showLists ? (
            <div>
              <div className="mt-m mx-s relative mb-1.5 flex">
                <FormProvider {...methods}>
                  <Input
                    autoFocus={false}
                    placeholder="Search"
                    value={searchQuery}
                    className="flex-1 pl-11"
                    onChange={(e) => onSearchQueryChange(e.target.value)}
                  />
                  <Search className="left-s absolute top-3.5 size-5 stroke-gray-300" />
                </FormProvider>
              </div>
              <TabsContent
                value={ConnectionsTabs.first}
                className="px-s [&::-webkit-scrollbar]:hidden"
              >
                {isLoading ? (
                  <SkeletonList
                    count={10}
                    className="mt-none gap-xs overflow-hidden"
                  />
                ) : firstListInfiniteScroll ? (
                  <VirtualizedScrolledList
                    list={firstList}
                    className={listClassName}
                    onClose={onClose}
                    infiniteScroll={firstListInfiniteScroll}
                    {...listProps}
                  />
                ) : (
                  <ScrolledList
                    {...{ onClose, ...listProps }}
                    list={firstList || []}
                    className={listClassName}
                  />
                )}
              </TabsContent>
              <TabsContent
                value={ConnectionsTabs.second}
                className="px-s [&::-webkit-scrollbar]:hidden"
              >
                {isLoading ? (
                  <SkeletonList
                    count={8}
                    className="mt-none gap-xs overflow-hidden"
                  />
                ) : secondListInfiniteScroll ? (
                  <VirtualizedScrolledList
                    list={secondList}
                    className={listClassName}
                    onClose={onClose}
                    infiniteScroll={secondListInfiniteScroll}
                    {...listProps}
                  />
                ) : (
                  <ScrolledList
                    {...{ onClose, ...listProps }}
                    list={secondList || []}
                    className={listClassName}
                  />
                )}
              </TabsContent>
            </div>
          ) : (
            <p className="py-xxl px-m text-center text-sm font-medium text-gray-500">
              {hiddenListMessage}
            </p>
          )}
        </Tabs>
      )}
    </CommonDialog>
  )
}

export default ConnectionsModal
